import { MultiSelect } from '@mantine/core';
import arrayMutators from 'final-form-arrays';
import { capitalize } from 'lodash';
import { useEffect, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { useToasts } from 'react-toast-notifications';
import Button from '../../../components/Button';
import Error from '../../../components/Error';
import { FormSection, FormSectionBody, FormSectionHeader, StyledForm } from '../../../components/Form';
import AutoSave from '../../../components/FormAutoSave';
import IconLock from '../../../components/icons/IconLock';
import { ROUTES } from '../../../routes/routes';
import { GatedContentConfiguration } from '../../../types/gatedContent';
import { StripeMode } from '../../../types/pricingPage';
import { StripePrice } from '../../../types/stripePrice';
import { formatFrequency, formatPrice } from '../../../utils/currency';
import { useAxios } from '../../../utils/hooks';
import { enumKeys } from '../../../utils/typescript';
import VALIDATORS from '../../../utils/validators';
import Footer from '../../PricingPages/NewPage/Footer';

interface Props {
    gatedContent: GatedContentConfiguration;
    onChange: (data: GatedContentConfiguration) => void;
}

export default function GatedContentForm({ gatedContent, onChange }: Props) {
    // FormAutoSave means we don't use the form submission process so store errors manually here
    const [submitError, setSubmitError] = useState<string>();
    const [stripeProducts, setStripeProducts] = useState<{ label: string; value: string }[]>([]);
    const { addToast } = useToasts();
    const axios = useAxios();

    useEffect(() => {
        if (stripeProducts.length > 0) {
            return;
        }

        enumKeys(StripeMode).forEach((mode) =>
            axios.current
                ?.get<StripePrice[]>(`/stripe/products/${mode}`)
                .then(({ data }) => {
                    setStripeProducts((products) => [
                        ...products,
                        ...data.map((price) => ({
                            label: `(${capitalize(mode)}) ${price.product.name} ${formatPrice(price)} ${formatFrequency(
                                price
                            )}`,
                            value: price.id,
                        })),
                    ]);
                })
                .catch((err) => {
                    if (err?.response?.status !== 400) {
                        addToast('Something went wrong. Please reload the page and try again', { appearance: 'error' });
                    } else {
                        setStripeProducts([]);
                    }
                })
        );
    }, [addToast, axios, stripeProducts.length]);

    const handleSave = async (values: GatedContentConfiguration) => {
        setSubmitError(undefined);
        try {
            await onChange(values);
        } catch (err) {
            setSubmitError('Error Saving.');
        }
    };
    return (
        <>
            <Form<GatedContentConfiguration>
                onSubmit={onChange}
                initialValues={gatedContent ?? { gatedDomains: [''], gatedPaths: [''] }}
                mutators={{
                    ...(arrayMutators as any),
                }}
            >
                {({
                    handleSubmit,
                    values,
                    invalid,
                    form: {
                        getFieldState,
                        mutators: { push },
                        change,
                    },
                }) => (
                    <>
                        <StyledForm onSubmit={handleSubmit} className="w-full mt-4">
                            <FormSection>
                                <FormSectionHeader>Description</FormSectionHeader>
                                <FormSectionBody>
                                    <div className="mb-2">
                                        <label className="text-sm text-gray-600">Name</label>
                                        <Field name="name" type="text" validate={VALIDATORS.required}>
                                            {(props) => (
                                                <>
                                                    <input
                                                        {...props.input}
                                                        className="input w-full"
                                                        placeholder="Paid Content"
                                                    />
                                                    {props.meta.error && (
                                                        <span className="ml-2">
                                                            <Error message={props.meta.error} />
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </Field>
                                    </div>
                                </FormSectionBody>
                            </FormSection>
                            <FormSection>
                                <FormSectionHeader>Content</FormSectionHeader>
                                <FormSectionBody>
                                    <div className="mb-2">
                                        <label className="text-sm text-gray-600">Domain</label>
                                        <FieldArray name="gatedDomains">
                                            {({ fields }) =>
                                                fields.map((name, index) => (
                                                    <div key={name}>
                                                        <Field
                                                            name={name}
                                                            component="input"
                                                            validate={VALIDATORS.requiredDomain}
                                                        >
                                                            {(props) => (
                                                                <>
                                                                    <div className="flex">
                                                                        <input
                                                                            {...props.input}
                                                                            className="input w-full"
                                                                            placeholder="google.com"
                                                                        />
                                                                    </div>
                                                                    {props.meta.error && (
                                                                        <span className="ml-2">
                                                                            <Error message={props.meta.error} />
                                                                        </span>
                                                                    )}
                                                                </>
                                                            )}
                                                        </Field>
                                                    </div>
                                                ))
                                            }
                                        </FieldArray>
                                    </div>
                                    {!getFieldState('gatedDomains')?.error &&
                                        values.gatedDomains.filter((domain) => !!domain).length > 0 && (
                                            <div className="mb-2">
                                                <label className="text-sm text-gray-600">Paths</label>
                                                <FieldArray name="gatedPaths">
                                                    {({ fields }) =>
                                                        fields.map((name, index) => (
                                                            <div key={name} className="py-1">
                                                                <div className="input-group mb-2">
                                                                    <span className="flex">
                                                                        <IconLock className="inline w-4 mr-1" />
                                                                        {values.gatedDomains[0]}
                                                                    </span>
                                                                    <Field
                                                                        name={`${name}.path`}
                                                                        component="input"
                                                                        validate={VALIDATORS.requiredUrlPath}
                                                                    >
                                                                        {(props) => (
                                                                            <input
                                                                                {...props.input}
                                                                                className="w-full"
                                                                                placeholder="/my-content"
                                                                            />
                                                                        )}
                                                                    </Field>
                                                                    <Field
                                                                        name={`${name}.matchType`}
                                                                        component="select"
                                                                    >
                                                                        {(props) => (
                                                                            <select
                                                                                {...props.input}
                                                                                className="select text-sm ml-2"
                                                                            >
                                                                                <option value="WILDCARD">
                                                                                    Starts With
                                                                                </option>
                                                                                <option value="EXACT">
                                                                                    Exact Match
                                                                                </option>
                                                                            </select>
                                                                        )}
                                                                    </Field>
                                                                    {index > 0 && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() =>
                                                                                window.confirm('Are you sure?')
                                                                                    ? fields.remove(index)
                                                                                    : null
                                                                            }
                                                                            title="Remove feature"
                                                                            className="pl-2"
                                                                        >
                                                                            <img
                                                                                src="/images/close.svg"
                                                                                alt=""
                                                                                className="w-6"
                                                                            />
                                                                        </button>
                                                                    )}
                                                                    {index === 0 && <div className="pl-2 w-8"></div>}
                                                                </div>
                                                                {getFieldState('gatedPaths')?.error?.[index]?.path && (
                                                                    <span className="ml-2">
                                                                        <Error
                                                                            message={
                                                                                getFieldState('gatedPaths')?.error[
                                                                                    index
                                                                                ].path
                                                                            }
                                                                        />
                                                                    </span>
                                                                )}
                                                                <div className="mb-2">
                                                                    <label className="text-sm text-gray-600">
                                                                        Required Stripe Products (optional)
                                                                    </label>
                                                                    <Field
                                                                        name={`${name}.stripePriceIds`}
                                                                        component="input"
                                                                    >
                                                                        {(props) => (
                                                                            <MultiSelect
                                                                                data={stripeProducts}
                                                                                searchable
                                                                                {...props.input}
                                                                            />
                                                                        )}
                                                                    </Field>
                                                                </div>
                                                            </div>
                                                        ))
                                                    }
                                                </FieldArray>
                                                <div className="flex flex-row-reverse py-2 pr-2">
                                                    <Button
                                                        type="button"
                                                        kind="tertiary"
                                                        onClick={() =>
                                                            push('gatedPaths', { path: '', matchType: 'WILDCARD' })
                                                        }
                                                    >
                                                        Add Path
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                </FormSectionBody>
                            </FormSection>
                            {!getFieldState('gatedDomains')?.error &&
                                values.gatedDomains.filter((domain) => !!domain).length > 0 && (
                                    <FormSection>
                                        <FormSectionHeader>Redirects</FormSectionHeader>
                                        <FormSectionBody>
                                            <div className="mb-2">
                                                <label className="text-sm text-gray-600">Unauthorized (optional)</label>
                                                <div className="text-sm pb-2">
                                                    Where to send the customer when they haven't subscribed
                                                </div>
                                                <Field name="authFailureRedirectUrl" type="text">
                                                    {(props) => (
                                                        <>
                                                            <div className="input-group">
                                                                <span>{values.gatedDomains[0]}</span>
                                                                <input
                                                                    {...props.input}
                                                                    className="input w-full"
                                                                    placeholder="/unauthorized"
                                                                />
                                                                {props.input.value && (
                                                                    <button
                                                                        onClick={() =>
                                                                            change('authFailureRedirectUrl', null)
                                                                        }
                                                                        className="pl-2"
                                                                    >
                                                                        <img
                                                                            src="/images/close.svg"
                                                                            alt=""
                                                                            className="w-6"
                                                                        />
                                                                    </button>
                                                                )}
                                                            </div>
                                                            {props.meta.error && (
                                                                <span className="ml-2">
                                                                    <Error message={props.meta.error} />
                                                                </span>
                                                            )}
                                                        </>
                                                    )}
                                                </Field>
                                            </div>
                                        </FormSectionBody>
                                    </FormSection>
                                )}
                            <div className="flex px-8">
                                <div className="mr-0 ml-auto">
                                    {submitError && <Error message={submitError} />}
                                    <AutoSave debounce={1000} save={handleSave} />
                                </div>
                            </div>
                        </StyledForm>
                        <Footer
                            next={{ href: ROUTES.GATED_CONTENT_SNIPPET, disabled: invalid, label: 'Next' }}
                            item={gatedContent}
                            size="LARGE"
                        />
                    </>
                )}
            </Form>
        </>
    );
}
